import {
  formatUtcForPocketBase,
  servedContractProvenance,
  servedFilter,
} from '../contracts/servedRecommendation.v1.js'
import { crossCountryFilter, MAX_ROWS } from './crossCountry.js'
import { parseScrapedAt } from './servedTime.js'
import { completeCollection } from './completeCollection.js'
import { classicReferenceFilter } from './classicResearch.js'
import { savedPlanFilter, exactSavedPlanRow } from './savedPlanReview.js'
import { checkYourselfClause, NET_LOSS_OK_CLAUSE } from './picks.js'

export { parseScrapedAt } from './servedTime.js'

/**
 * PocketBase backend for car-import-frontend.
 *
 * PocketBase REST API:
 *   List:   GET /api/collections/{collection}/records?filter=...&sort=...&page=...&perPage=...
 *   Filter: field = 'value', field > 0, field ~ 'value' (LIKE), field != null
 *   Sort:   +field (asc) or -field (desc), comma-separated for multiple
 *
 * Every search filter runs on the server (SIMPLE-1): one page for the browse-all view, all
 * pages for the small picks list and the map.
 */

// In production (HTTPS on Vercel), use the /pb-api rewrite to avoid mixed-content blocking.
// In development (localhost HTTP), call PocketBase directly.
// Read the env bag defensively rather than `import.meta.env.VITE_…` directly: Vite substitutes
// either form at build time, but only this one also loads under bare Node, which is how the
// FEAT-4 contract test drives the REAL lookup path (kentekenApi -> here -> PocketBase).
// The default backend comes from the served contract's provenance, never a hardcoded host.
const _env = (typeof import.meta !== 'undefined' && import.meta.env) || globalThis.process?.env || {}
const _configuredUrl = _env.VITE_POCKETBASE_URL || servedContractProvenance().servedBackend
const POCKETBASE_URL = (typeof window !== 'undefined' && window.location.protocol === 'https:')
  ? '/pb-api'
  : _configuredUrl
const COLLECTION = 'car_table'
const MAX_RECORDS = 5000 // Safety cap for all-pages fetching

export const fetchClassicReferences = async (car, now = new Date()) => {
  const result = await pbFetch('/api/collections/classic_nl_references/records', {
    filter: classicReferenceFilter(car, now), perPage: 500, page: 1, sort: 'year,id',
  }, { timeoutMs: 15000, cache: 'no-store' })
  if (!Array.isArray(result.items) || !Number.isInteger(result.totalItems) || result.totalItems < result.items.length) throw new Error('Incomplete Dutch reference response')
  return { rows: result.items || [], total: result.totalItems, truncated: result.totalItems > (result.items || []).length }
}

/** A saved plan carries personal inputs only. Fetch the exact current published
 * listing before displaying any calculation; never revive a cached price/band. */
export async function fetchSavedPlanReview(draft, now = new Date()) {
  const response = await pbFetch('/api/collections/car_table/records', {
    filter: savedPlanFilter(draft, now), page: 1, perPage: 2, sort: 'id',
  }, { timeoutMs: 15000, cache: 'no-store' })
  const car = exactSavedPlanRow(draft, response, now)
  if (!car) return { car, checkedAt: now.toISOString() }
  if (draft.identity.kind === 'purchase') {
    // Personal budget review is not a recommendation surface. Preserve all
    // canonical recommendation gates in a separate bounded eligibility query.
    let recommendationCurrent = false
    try {
      const eligible = await pbFetch('/api/collections/car_table/records', {
        filter: savedPlanFilter(draft, now, true), page: 1, perPage: 2,
        fields: 'id,listing_id,country_code,decision_snapshot_id,model_git_sha,price_amount_eur',
      }, { timeoutMs: 15000, cache: 'no-store' })
      recommendationCurrent = typeof car.id === 'string' && car.id.length > 0 && eligible.totalItems === 1 && eligible.items?.length === 1 &&
        ['id', 'listing_id', 'country_code', 'decision_snapshot_id', 'model_git_sha', 'price_amount_eur']
          .every(key => eligible.items[0][key] === car[key])
    } catch { /* Missing eligibility evidence is never a current recommendation. */ }
    return { car, recommendationCurrent, checkedAt: now.toISOString() }
  }
  const [references, sourceReviews] = await Promise.all([
    fetchClassicReferences(car, now),
    typeof window === 'undefined' ? null : fetch('/classic-source-review.json', { cache: 'no-store', signal: AbortSignal.timeout(5000) })
      .then(response => response.ok ? response.json() : null).catch(() => null),
  ])
  return { car, references, sourceReviews, checkedAt: now.toISOString() }
}

// ─── helpers ────────────────────────────────────────────────────────────────

/** Fetch JSON from PocketBase with error handling. */
async function pbFetch(path, params = {}, { timeoutMs, cache } = {}) {
  const fullPath = `${POCKETBASE_URL}${path}`
  const url = fullPath.startsWith('http') ? new URL(fullPath) : new URL(fullPath, window.location.origin)
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v)
  })

  const resp = await fetch(url.toString(), timeoutMs || cache ? { ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}), ...(cache ? { cache } : {}) } : undefined)
  if (!resp.ok) {
    const text = await resp.text()
    throw new Error(`PocketBase error ${resp.status}: ${text.slice(0, 300)}`)
  }
  return resp.json()
}

/** Escape a string for PocketBase filter expressions. */
function esc(val) {
  return String(val).replace(/'/g, "\\'")
}

/** Strip PocketBase internal fields from a record. */
function stripPbFields({ id: _id, collectionId: _cid, collectionName: _cn, created: _cr, updated: _up, ...rest }) {
  return rest
}

/** Deduplicate records by listing_id. */
function dedup(items) {
  const seen = new Set()
  return items.filter(item => {
    if (!item.listing_id) return true
    if (seen.has(item.listing_id)) return false
    seen.add(item.listing_id)
    return true
  })
}

// ─── filter builder ─────────────────────────────────────────────────────────

/** The buyer's own vehicle and budget clauses (make, model, country, fuel, year, price,
 * mileage), shared by the result query and the opinion counts so both describe one selection. */
export function vehicleClauses(filters) {
  const parts = []
  // Brand
  if (filters.make && filters.make !== '') {
    parts.push(`brand = '${esc(filters.make.trim())}'`)
  }

  // Model (multi-select)
  if (filters.model && Array.isArray(filters.model) && filters.model.length > 0) {
    const modelParts = filters.model.map(m => `model = '${esc(m.trim())}'`)
    parts.push(`(${modelParts.join(' || ')})`)
  } else if (filters.model && typeof filters.model === 'string' && filters.model !== '') {
    parts.push(`model = '${esc(filters.model.trim())}'`)
  }

  // Country filter (multi-select)
  if (filters.countries && filters.countries.length > 0) {
    const countryParts = filters.countries.map(c => `country_code = '${esc(c.toUpperCase())}'`)
    parts.push(`(${countryParts.join(' || ')})`)
  }

  // Fuel type filter (multi-select, expands canonical names to all raw DB variants)
  if (filters.fuel && Array.isArray(filters.fuel) && filters.fuel.length > 0) {
    const fuelParts = []
    for (const f of filters.fuel) {
      const variants = _fuelTypeVariants[f] || [f]
      for (const v of variants) {
        fuelParts.push(`fuel_type = '${esc(v)}'`)
      }
    }
    parts.push(`(${fuelParts.join(' || ')})`)
  }

  // Year range
  if (filters.minYear && filters.minYear !== '') {
    const v = parseInt(filters.minYear, 10)
    if (!isNaN(v)) parts.push(`year >= ${v}`)
  }
  if (filters.maxYear && filters.maxYear !== '') {
    const v = parseInt(filters.maxYear, 10)
    if (!isNaN(v)) parts.push(`year <= ${v}`)
  }

  // Price range
  if (filters.minPrice && filters.minPrice !== '') {
    const v = parseFloat(filters.minPrice)
    if (!isNaN(v)) parts.push(`price_amount_eur >= ${v}`)
  }
  if (filters.maxPrice && filters.maxPrice !== '') {
    const v = parseFloat(filters.maxPrice)
    if (!isNaN(v)) parts.push(`price_amount_eur <= ${v}`)
  }

  // Mileage range
  if (filters.minMileage && filters.minMileage !== '') {
    const v = parseInt(filters.minMileage, 10)
    if (!isNaN(v)) parts.push(`mileage_km >= ${v}`)
  }
  if (filters.maxMileage && filters.maxMileage !== '') {
    const v = parseInt(filters.maxMileage, 10)
    if (!isNaN(v)) parts.push(`mileage_km <= ${v}`)
  }
  return parts
}

/**
 * SIMPLE-1: three lists, each one server query with the buyer's own vehicle and budget filters.
 *   koopkans    the contract's recommendation surface (dealer page live < 24 h, every check passed)
 *   controleer  the discovery population, model-approved but not on that surface (checkYourselfClause)
 *   all         the discovery population: every priced car we serve
 * Every condition runs on the server, so no client-side copy of a filter can drift out of step.
 */
export function buildServerFilter(filters = {}, now = new Date()) {
  const vehicle = vehicleClauses(filters)
  if (filters.mode === 'koopkans') return [servedFilter('recommendation', now), NET_LOSS_OK_CLAUSE, ...vehicle].join(' && ')
  return [servedFilter('discovery', now), ...vehicle,
    ...(filters.mode === 'controleer' ? [checkYourselfClause(now)] : [])].join(' && ')
}

/** Safe profit first: ranking by the headline figure would put our biggest over-estimates on
 * top (selecting the largest estimated margins also selects the largest valuation errors). A
 * row from an older release has no stored profit; it sorts after the rows that do. */
export const SORTS = Object.freeze({
  safe: '-profit_safe_eur,-downside_eur',
  expected: '-profit_expected_eur,-expected_revenue',
  price_low: '+price_amount_eur',
  newest: '+days_online',
})
export const buildServerSort = sortBy => SORTS[sortBy] || SORTS.safe

/**
 * Fetch ALL matching records from PocketBase by paginating through server pages.
 * Used by the picks list and the map.
 */
async function fetchAllServerPages(filter, sort) {
  const batchSize = 500
  const first = await pbFetch(`/api/collections/${COLLECTION}/records`, {
    filter: filter || undefined,
    sort,
    perPage: batchSize,
    page: 1,
  })
  const allData = [...(first.items || [])]
  const serverTotal = first.totalItems || 0
  if (allData.length < batchSize || allData.length >= serverTotal) {
    return dedup(allData.map(stripPbFields))
  }
  const totalPages = Math.min(Math.ceil(serverTotal / batchSize), Math.ceil(MAX_RECORDS / batchSize))
  const results = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) =>
    pbFetch(`/api/collections/${COLLECTION}/records`, { filter: filter || undefined, sort, perPage: batchSize, page: i + 2 })))
  for (const result of results) allData.push(...(result.items || []))
  return dedup(allData.map(stripPbFields))
}

function verifiedQueryError(error) {
  return new Error(
    `Verified car query failed. No broader fallback results were shown. ${error.message}`,
    { cause: error }
  )
}

// ─── exported search functions ──────────────────────────────────────────────

/** One server page of the selection (the "every priced car" view). */
export const searchCarListings = async (filters = {}, page = 1, pageSize = 25, sortBy = 'safe') => {
  try {
    const result = await pbFetch(`/api/collections/${COLLECTION}/records`, {
      filter: buildServerFilter(filters), sort: buildServerSort(sortBy), perPage: pageSize, page,
    })
    const totalCount = result.totalItems || 0
    return { data: dedup((result.items || []).map(stripPbFields)), totalCount, page, pageSize,
      totalPages: Math.ceil(totalCount / pageSize) }
  } catch (error) {
    console.error('[PocketBase] Error fetching car_table:', error)
    throw verifiedQueryError(error)
  }
}

/** Every row of the selection (the picks list, which is small, and the map). */
export const searchAllCarListings = async (filters = {}, sortBy = 'safe') => {
  try {
    return await fetchAllServerPages(buildServerFilter(filters), buildServerSort(sortBy))
  } catch (error) {
    console.error('[PocketBase] Error fetching all car listings:', error)
    throw verifiedQueryError(error)
  }
}

export const getCarBrands = async () => {
  try {
    // Try pre-computed lookup collection (1 API call)
    try {
      const result = await pbFetch('/api/collections/car_brands/records', {
        perPage: 500,
        sort: '+name',
      })
      const items = result.items || []
      if (items.length > 0) {
        const brands = items.map(item => item.name).filter(Boolean)
        console.log(`[PocketBase] Got ${brands.length} brands from lookup`)
        return brands
      }
    } catch (e) {
      console.warn('[PocketBase] car_brands lookup unavailable, falling back to sampling')
    }

    // Fallback: sample from car_table with multiple sort orderings
    const allBrands = new Set()
    for (const sort of ['+brand', '-brand', '+year', '-year', '+listing_id', '-listing_id']) {
      try {
        const result = await pbFetch(`/api/collections/${COLLECTION}/records`, {
          filter: `brand != null && brand != ''`,
          fields: 'brand',
          sort,
          perPage: 500,
          page: 1,
        })
        for (const item of (result.items || [])) {
          if (item.brand?.trim()) allBrands.add(item.brand.trim())
        }
      } catch (err) {
        // skip failed sort strategy
      }
    }

    const brands = Array.from(allBrands).sort((a, b) => a.localeCompare(b))
    console.log(`[PocketBase] Found ${brands.length} brands via sampling fallback`)
    return brands
  } catch (error) {
    console.error('[PocketBase] Error fetching brands:', error)
    return []
  }
}

export const getCarModels = async (make) => {
  try {
    if (!make) return []

    const allModels = new Set()

    // 1. Fast: try lookup collection first
    try {
      const result = await pbFetch('/api/collections/car_models/records', {
        filter: `brand = '${esc(make)}'`,
        perPage: 500,
        sort: '+name',
      })
      for (const item of (result.items || [])) {
        if (item.name?.trim()) allModels.add(item.name.trim())
      }
      if (allModels.size > 0) {
        console.log(`[PocketBase] Got ${allModels.size} models from lookup`)
      }
    } catch (e) {
      // lookup collection unavailable, continue to sampling
    }

    // 2. Supplement with car_table samples using different sorts to catch missing models
    const brandFilter = `brand = '${esc(make)}'`
    for (const sort of ['+model', '-model', '+listing_id', '-listing_id', '+year', '-year']) {
      try {
        const result = await pbFetch(`/api/collections/${COLLECTION}/records`, {
          filter: brandFilter,
          fields: 'model',
          sort,
          perPage: 500,
          page: 1,
        })
        for (const item of (result.items || [])) {
          if (item.model?.trim()) allModels.add(item.model.trim())
        }
      } catch (err) {
        // skip failed sort strategy
      }
    }

    const models = Array.from(allModels).sort()
    console.log(`[PocketBase] Found ${models.length} models for ${make} (lookup + sampling)`)
    return models
  } catch (error) {
    console.error('[PocketBase] Error fetching models:', error)
    return []
  }
}

// SIMPLE-1: the pipeline normalises fuel_type to four values (benzine 360k, diesel 168k,
// hybride 101k, elektrisch 42k served rows on 2026-10-13), so the fuel options are that list.
// The old loader sampled 6 x 500 rows on every page load (~5 s each under load) to rediscover it.
// shortcut: a new pipeline fuel class must be added here; do it when FUEL-VOCAB-1 adds LPG/CNG.
const SERVED_FUELS = Object.freeze({ Petrol: ['benzine'], Diesel: ['diesel'], Hybrid: ['hybride'], Electric: ['elektrisch'] })
const _fuelTypeVariants = SERVED_FUELS

export const getCarFuelTypes = async () => Object.keys(SERVED_FUELS)

const countryNameMap = {
  'AT': 'Austria', 'CH': 'Switzerland', 'DE': 'Germany', 'DK': 'Denmark',
  'ES': 'Spain', 'FI': 'Finland', 'FR': 'France', 'IT': 'Italy',
  'PL': 'Poland', 'PT': 'Portugal', 'RO': 'Romania', 'SE': 'Sweden',
  'UK': 'United Kingdom'
}

// ─── Market Analytics ────────────────────────────────────────────────────────

/**
 * Fetch pre-computed market analytics (brand + model level stats).
 * Returns { brands: [...rollup rows], models: { brandName: [...model rows] } }
 */
export const fetchMarketAnalytics = async () => {
  try {
    const allData = (await completeCollection(params => pbFetch(
      // MARKET-BYTES-1: every field, but `evidence` (54% of the bytes, read only for two flags the
      // backend now publishes as fields) cut to one character: 1.1 MB -> 0.51 MB a page.
      '/api/collections/market_analytics/records', { ...params, sort: 'brand,model,id', fields: '*,evidence:excerpt(1)' },
    ))).map(stripPbFields)

    const brands = allData.filter(r => r.model === '_ALL_')
    const models = {}
    for (const r of allData) {
      if (r.model !== '_ALL_') {
        if (!models[r.brand]) models[r.brand] = []
        models[r.brand].push(r)
      }
    }

    console.log(`[PocketBase] Market analytics: ${brands.length} brands, ${Object.keys(models).length} brand groups`)
    return { brands, models }
  } catch (error) {
    console.error('[PocketBase] Error fetching market analytics:', error)
    throw error
  }
}

/**
 * Fetch the empirical variant layer (market_variants) for one make.
 *
 * Rows carry a real price band (p5/p25/p50/p75/p95) built from NL comparables,
 * at three grains: 'model' (all fuels), 'model_fuel', and 'variant_fuel'.
 * Thin variants are never published (the builder enforces MIN_VARIANT_LISTINGS),
 * so an empty result means "not enough evidence", not "no such car".
 */
export const fetchMarketVariants = async (make, { fresh = false } = {}) => {
  if (!make) return []
  try {
    const rows = (await completeCollection(params => pbFetch(
      '/api/collections/market_variants/records',
      { ...params, filter: `make = '${esc(make)}'`, sort: 'model,variant,id' },
      fresh ? { timeoutMs: 15000, cache: 'no-store' } : {},
    ))).map(stripPbFields)

    console.log(`[PocketBase] Market variants for ${make}: ${rows.length} rows`)
    return rows
  } catch (error) {
    console.error('[PocketBase] Error fetching market variants:', error)
    throw error
  }
}

/**
 * XMARKET-1: the per-country cells for one make (make, model, fuel, model-year band, country).
 *
 * Same shape of fetch as fetchMarketVariants: whole collection for the make, through
 * completeCollection so a half-loaded population can never render as a thin market. Roughly
 * 500 to 1,400 rows per make, three pages at most.
 */
export const fetchMarketCountryCells = async (make) => {
  if (!make) return []
  try {
    const rows = (await completeCollection(params => pbFetch(
      '/api/collections/market_country_cells/records',
      { ...params, filter: `make = '${esc(make)}'`, sort: 'model,year_band,country_code,id' },
    ))).map(stripPbFields)
    console.log(`[PocketBase] Market cells for ${make}: ${rows.length} rows`)
    return rows
  } catch (error) {
    // The collection does not exist until the builder has run once. An empty cross-market
    // panel is the correct rendering of that; the rest of the tab must not go down with it.
    console.warn('[PocketBase] Market country cells unavailable:', error.message)
    return []
  }
}

/** The weekly departure rate per (brand, model, country), where it was measurable at all. */
export const fetchMarketDemandByCountry = async (brand) => {
  if (!brand) return []
  try {
    const rows = (await completeCollection(params => pbFetch(
      '/api/collections/market_analytics_country/records',
      { ...params, filter: `brand = '${esc(brand)}'`, sort: 'model,country_code,id' },
    ))).map(stripPbFields)
    return rows
  } catch (error) {
    console.warn('[PocketBase] Per-country demand unavailable:', error.message)
    return []
  }
}

export const fetchMarketTracking = async (brand) => (
  await completeCollection(params => pbFetch('/api/collections/market_tracking/records', {
    ...params,
    filter: brand ? `brand = '${esc(brand)}'` : "model = '_ALL_' && country_code = '_ALL_'",
    sort: 'brand,model,country_code,id',
  }))
).map(stripPbFields)

/** The best route per (make, model) -- the brand table reads this, so it loads at mount. */
export const fetchMarketModelRoutes = async () => {
  try {
    const rows = (await completeCollection(params => pbFetch(
      '/api/collections/market_model_routes/records', { ...params, sort: 'make,model,id' },
    ))).map(stripPbFields)
    console.log(`[PocketBase] Market model routes: ${rows.length} rows`)
    return rows
  } catch (error) {
    console.warn('[PocketBase] Market model routes unavailable:', error.message)
    return []
  }
}

/** The continent-wide ranking, at most a few hundred rows. */
export const fetchMarketTopRoutes = async () => {
  try {
    const rows = (await completeCollection(params => pbFetch(
      '/api/collections/market_routes_top/records', { ...params, sort: '-gap_lower_pct,id' },
    ))).map(stripPbFields)
    console.log(`[PocketBase] Market top routes: ${rows.length} rows`)
    return rows
  } catch (error) {
    console.warn('[PocketBase] Market top routes unavailable:', error.message)
    return []
  }
}

/**
 * The distinct `make` values present in `market_variants`, as stored.
 *
 * Deliberately NOT read from the `car_brands` lookup: measured 2026-08-24, that collection
 * holds 89 names and is missing both `Mercedes` and `VW` — our two largest brands — while
 * carrying `SEAT` and `Seat` as separate rows. Resolving a plate's make through it would
 * silently return "we don't publish that brand" for every Mercedes and Volkswagen in the
 * country. The rows we are about to join against are the only vocabulary that cannot
 * disagree with itself.
 *
 * Cached per session: it is ~20 tiny requests and the answer changes only when a brand is
 * added to the fleet.
 */
let _marketVariantMakes = null
/**
 * KENTEKEN-XCOUNTRY-1: every scraped listing of one car (make/model/fuel, year ± 2) with the
 * pipeline's import costs, at home and abroad, cheapest first. Up to MAX_ROWS rows (3 pages of
 * 500); `truncated` says when the model is bigger than that, so the caller can label the
 * medians as a sample. Measured 2026-09-02: ~0.15 s for a 443-row model.
 */
export const fetchCrossCountryRows = async ({ make, model, fuel, year, mileageKm, hpBand }) => {
  const filter = crossCountryFilter({ make, model, fuel, year, mileageKm, hpBand })
  const fields = 'country_code,price_amount_eur,bpm_rest,import_fixed_costs,potential_vat_liability,is_vat_new,year,mileage_km'
  const rows = []
  let totalItems = 0
  for (let page = 1; page <= Math.ceil(MAX_ROWS / 500); page++) {
    const result = await pbFetch('/api/collections/car_table/records', {
      perPage: 500, page, filter, fields, sort: '+price_amount_eur', skipTotal: 0,
    })
    totalItems = result.totalItems || totalItems
    rows.push(...(result.items || []))
    if ((result.items || []).length < 500) break
  }
  return { rows, totalItems, truncated: totalItems > rows.length }
}

export const fetchMarketVariantMakes = async ({ fresh = false } = {}) => {
  if (_marketVariantMakes && !fresh) return _marketVariantMakes
  try {
    const makes = new Set()
    let page = 1
    while (true) {
      const result = await pbFetch('/api/collections/market_variants/records', {
        perPage: 500,
        page,
        fields: 'make',
        sort: 'make',
      }, fresh ? { timeoutMs: 15000, cache: 'no-store' } : {})
      const items = result.items || []
      items.forEach(r => r.make && makes.add(r.make))
      if (items.length < 500 || page * 500 >= (result.totalItems || 0)) break
      page++
    }
    _marketVariantMakes = [...makes]
    console.log(`[PocketBase] Market variant makes: ${_marketVariantMakes.length}`)
    return _marketVariantMakes
  } catch (error) {
    console.error('[PocketBase] Error fetching market variant makes:', error)
    if (fresh) throw error // A failed fresh lookup is not "we have no model".
    return []
  }
}

/**
 * Fetch sold car listings from the sold_cars collection.
 */
export const fetchSoldCars = async ({ brand, model, page = 1, perPage = 25, sort = '-sold_at' } = {}) => {
  try {
    const filterParts = []
    if (brand) filterParts.push(`brand = '${esc(brand)}'`)
    if (model) filterParts.push(`model = '${esc(model)}'`)

    const params = { perPage, page, sort }
    if (filterParts.length > 0) params.filter = filterParts.join(' && ')

    const result = await pbFetch('/api/collections/sold_cars/records', params)
    return {
      items: (result.items || []).map(stripPbFields),
      totalItems: result.totalItems || 0,
      totalPages: result.totalPages || 0,
    }
  } catch (error) {
    console.error('[PocketBase] Error fetching sold cars:', error)
    return { items: [], totalItems: 0, totalPages: 0 }
  }
}

export const getCarCountries = async () => {
  try {
    // Try pre-computed lookup collection (1 API call)
    try {
      const result = await pbFetch('/api/collections/car_countries/records', {
        perPage: 500,
        sort: '+name',
      })
      const items = result.items || []
      if (items.length > 0) {
        const countries = items.map(item => ({
          code: item.code,
          name: item.name || countryNameMap[item.code] || item.code,
        }))
        console.log(`[PocketBase] Got ${countries.length} countries from lookup`)
        return countries
      }
    } catch (e) {
      console.warn('[PocketBase] car_countries lookup unavailable, falling back to pagination')
    }

    // Fallback: paginate through car_table
    const allCountries = new Map()
    for (let page = 1; page <= 20; page++) {
      const result = await pbFetch(`/api/collections/${COLLECTION}/records`, {
        filter: `country_code != null && country_code != ''`,
        fields: 'country_code,country_name',
        sort: '+country_name',
        perPage: 500,
        page,
      })
      const items = result.items || []
      if (items.length === 0) break
      for (const item of items) {
        const code = item.country_code?.toUpperCase()
        if (code && !allCountries.has(code)) {
          const name = item.country_name || countryNameMap[code] || code
          allCountries.set(code, { code, name })
        }
      }
    }

    const countries = Array.from(allCountries.values()).sort((a, b) => a.name.localeCompare(b.name))
    console.log(`[PocketBase] Found ${countries.length} countries via fallback`)
    return countries
  } catch (error) {
    console.error('[PocketBase] Error fetching countries:', error)
    return []
  }
}
